import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { BsTrash } from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  SimpleGrid,
  Text
} from '@chakra-ui/react';

import FormInput from '../../../../components/form/FormInput';
import { ConfirmActionWarningModal } from '../../../../components/library';
import StackedButtonGroup from '../../../../components/Modal/ModalButtonGroups/StackedButtonGroup';
import LoadingSpinner from '../../../../components/Spinner';
import { createErrorToast } from '../../../../redux/modules/app';
import { hideModal, showModal } from '../../../../redux/modules/modal';
import {
  useCompanyCustomRoles,
  useCompanyPermissions,
  useCreateCompanyRole,
  useDeleteCompanyRole
} from '../../../../services/company-service';
import boxStyles from '../../../../styles/boxStyles';
import colors from '../../../../styles/theme/foundation/colors';
import { unique } from '../../../../utils/arrayUtil';

import RoleViewEditModal from './components/RoleViewEditModal';
import { groupedPermissions } from './permissions';

/**
 * Convert a permission name like "create_users" → "Create Users".
 * Used as a fallback when the API does not return a display_name.
 */
const humanizeName = name => name
  .split('_')
  .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
  .join(' ');

const ALWAYS_GRANTED_TOOLTIP = 'This permission is included in every role and cannot be turned off.';

/**
 * Build the permission group arrays from the API response.
 * Each item in `apiPermissions` has: { name, displayName, description, scope, category, alwaysGranted },
 * where `scope` is an array of 'company' | 'department'.
 * We group by category. A permission scoped to both appears once, under company.
 * Always-granted permissions render as permanently-on, disabled switches.
 */
const buildPermissionGroups = apiPermissions => {
  const companyGroups = {};
  const departmentGroups = {};

  apiPermissions.forEach(perm => {
    const labelName = perm.displayName || humanizeName(perm.name);
    const alwaysGranted = !!perm.alwaysGranted;
    const toolTip = alwaysGranted ? ALWAYS_GRANTED_TOOLTIP : (perm.description || '');
    const entry = { inputName: perm.name, labelName, toolTip, alwaysGranted };
    const category = perm.category || 'Other';
    const scopes = Array.isArray(perm.scope) ? perm.scope : [perm.scope];

    if (scopes.includes('company')) {
      if (!companyGroups[category]) {
        companyGroups[category] = { groupId: category, groupLabel: category, permissions: [] };
      }

      companyGroups[category].permissions.push(entry);
    } else {
      if (!departmentGroups[category]) {
        departmentGroups[category] = { groupId: category, groupLabel: category, permissions: [] };
      }

      departmentGroups[category].permissions.push(entry);
    }
  });

  return {
    companyPermissions: Object.values(companyGroups),
    departmentPermissions: Object.values(departmentGroups)
  };
};

const CustomRolesPanel = () => {
  const { companyId } = useParams();
  const dispatch = useDispatch();
  const [creating, setCreating] = useState(false);

  const { data, isLoading } = useCompanyCustomRoles(companyId, { include: 'permissions' });
  const { data: apiPermissions, isLoading: isLoadingPermissions } = useCompanyPermissions(companyId);
  const { mutate: createRole, isLoading: isCreating } = useCreateCompanyRole({ companyId });
  const { mutate: deleteRole } = useDeleteCompanyRole({ companyId });

  const useFormMethods = useForm();
  const { handleSubmit, setValue, reset } = useFormMethods;

  // Build permission groups from the API response; fall back to empty arrays while loading
  const { companyPermissions, departmentPermissions } = apiPermissions
    ? buildPermissionGroups(apiPermissions)
    : { companyPermissions: [], departmentPermissions: [] };

  const onSubmit = formData => {
    const { name, description } = formData;

    delete formData.name;
    delete formData.description;

    const selectedPermissions = Object.keys(formData).filter(perm => formData[perm]);

    const selectedGroupedPermissions = Object.keys(groupedPermissions).reduce((groupedArray, perm) => {
      if (formData[perm]) {
        groupedArray = [...groupedArray, ...groupedPermissions[perm]];
      }

      return groupedArray;
    }, []);

    const allPermissions = unique([...selectedPermissions, ...selectedGroupedPermissions]);

    if (data.find(role => role.name === name.replace(/\s+/g, '_').toUpperCase())) {
      dispatch(createErrorToast({ title: 'A role with that name already exists.', description: 'Please use a different name' }));
    } else {
      createRole({ name, description, permissions: allPermissions });

      setCreating(false);
    }
  };

  const onDelete = roleId => {
    dispatch(showModal(
      () => <ConfirmActionWarningModal description="Are you sure you want to permanently delete this role?" />,
      {
        title: 'Delete Role?',
        size: 'md',
        buttons: {
          component: StackedButtonGroup,
          primaryButtonOptions: {
            label: 'Delete',
            isDisabled: true,
            onClick: async () => {
              deleteRole({ roleId });
              dispatch(hideModal());
            }
          }
        }
      }
    ));
  };

  const onViewRole = (role, openInEdit = false) => {
    dispatch(showModal(
      () => (
        <RoleViewEditModal
          companyId={companyId}
          companyPermissionGroups={companyPermissions}
          departmentPermissionGroups={departmentPermissions}
          isEditing={openInEdit}
          role={role}
        />
      ),
      {
        title: role.displayName || role.name,
        size: '4xl'
      }
    ));
  };

  const bulkClickPermissions = shouldCheck => {
    [...companyPermissions, ...departmentPermissions].map(group => group.permissions.map(perm => {
      // Always-granted permissions cannot be toggled
      if (perm.alwaysGranted) {
        return;
      }

      const selector = `input[name=${perm.inputName}]`;
      const switchInput = document.querySelector(selector);
      const isChecked = switchInput.checked;

      if (shouldCheck && !isChecked) {
        switchInput.click();
      } else if (!shouldCheck && isChecked) {
        switchInput.click();
      }

      setValue(perm.inputName, shouldCheck);
    }));
  };

  if (isLoading) {
    return (
      <Box layerStyle="tabPanel">
        <LoadingSpinner isInline minHeight="150px" />
      </Box>
    );
  }

  if (creating) {
    return (
      <Box layerStyle="tabPanel">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box borderBottom="1px solid" borderBottomColor="greyScale.200" p={8}>
            <Text mb={4} textAlign="left" textStyle="bodyMd500">Define Your Custom Role</Text>
            <HStack alignItems="flex-start" justifyContent="space-between">
              <Box minW="400px" pr={8} textAlign="left">
                <FormInput
                  required
                  inputName="name"
                  labelName="Give your role a name"
                  placeholder="Department Auditor"
                  useFormMethods={useFormMethods}
                />
                <FormInput
                  inputName="description"
                  labelName="Give your role a description"
                  placeholder="This role is for our auditors in zone A"
                  type="textAreaSm"
                  useFormMethods={useFormMethods}
                />
              </Box>

              <HStack>
                <Button
                  isLoading={isCreating}
                  type="submit"
                  variant="primary"
                >
                  Create Role
                </Button>

                <Button
                  variant="secondary"
                  onClick={() => setCreating(false)}
                >
                  Cancel
                </Button>
              </HStack>
            </HStack>
          </Box>

          <Box borderBottom="1px solid" borderBottomColor="greyScale.200" p={8}>
            <HStack mb={8}>
              <Button
                isDisabled={isLoadingPermissions}
                onClick={() => bulkClickPermissions(true)}
              >
                Select All Permissions
              </Button>

              <Button
                isDisabled={isLoadingPermissions}
                variant="secondary"
                onClick={() => bulkClickPermissions(false)}
              >
                Deselect All Permissions
              </Button>
            </HStack>
            <Text mb={2} textAlign="left" textStyle="bodyMd500">Company Tier Permissions</Text>
            <Text mb={2} textAlign="left" textStyle="bodySm">
              These are permissions that are not affected by department assignment.  For example, if a user
              is assigned this role for a specific department, the user will be able to perform these permissions regardless of which department is selected.
            </Text>

            {isLoadingPermissions ? (
              <LoadingSpinner isInline minHeight="80px" />
            ) : (
              companyPermissions.map(group => (
                <Box borderBottom="1px solid" borderBottomColor="greyScale.200" key={group.groupId} p={8}>
                  <Text
                    mb={8}
                    textAlign="left"
                    textDecoration="underline"
                    textStyle="bodyMd500"
                  >
                    {group.groupLabel}
                  </Text>

                  <SimpleGrid columns={2} spacing={1}>
                    {group.permissions.map(perm => (
                      <Box key={perm.inputName}>
                        <FormInput
                          inputName={perm.inputName}
                          isDisabled={perm.alwaysGranted}
                          justifyContent="space-between"
                          labelName={perm.labelName}
                          pr={4}
                          textAlign="left"
                          toolTip={perm.toolTip}
                          type="switch"
                          useFormMethods={useFormMethods}
                        />
                      </Box>
                    ))}
                  </SimpleGrid>
                </Box>
              ))
            )}
          </Box>

          <Box borderBottom="1px solid" borderBottomColor="greyScale.200" p={8}>
            <Text mb={2} textAlign="left" textStyle="bodyMd500">Department Tier Permissions</Text>
            <Text mb={2} textAlign="left" textStyle="bodySm">
              These are permissions that are affected by department assignment.  For example, if a user
              is assigned this role for a specific department, the user will only be able to perform these
              actions on the selected department unless they are assigned an appropriate company role.
            </Text>

            {isLoadingPermissions ? (
              <LoadingSpinner isInline minHeight="80px" />
            ) : (
              departmentPermissions.map(group => (
                <Box borderBottom="1px solid" borderBottomColor="greyScale.200" key={group.groupId} p={8}>
                  <Text
                    mb={8}
                    textAlign="left"
                    textDecoration="underline"
                    textStyle="bodyMd500"
                  >
                    {group.groupLabel}
                  </Text>

                  <SimpleGrid columns={2} spacing={1}>
                    {group.permissions.map(perm => (
                      <Box key={perm.inputName}>
                        <FormInput
                          inputName={perm.inputName}
                          isDisabled={perm.alwaysGranted}
                          justifyContent="space-between"
                          labelName={perm.labelName}
                          pr={4}
                          textAlign="left"
                          toolTip={perm.toolTip}
                          type="switch"
                          useFormMethods={useFormMethods}
                        />
                      </Box>
                    ))}
                  </SimpleGrid>
                </Box>
              ))
            )}
          </Box>
        </form>
      </Box>
    );
  }

  return (
    <Box layerStyle="tabPanel">
      <Box borderBottom="1px solid" borderBottomColor="greyScale.200" p={8}>
        <HStack justifyContent="space-between">
          <Box pr={12} textAlign="left">
            <Text mb={4} textStyle="bodyMd500">Create Custom Roles</Text>
            <Text color="greyScale.500" mb={2} textStyle="bodySm">
              Here you can create, edit, and remove custom roles.  Custom roles allow you to select the granular permissions you&apos;d like to grant to your users.
            </Text>

            <Text color="greyScale.500" textStyle="bodySm">
              Please note: when deleting a custom role, PINS will automatically assign the VIEWER role to any active users who were assigned that custom role.
            </Text>
          </Box>
          <Box>
            <Button
              isLoading={false}
              variant="primary"
              onClick={() => {
                // Clear any values left over from a previous create; always-granted
                // permissions start (and stay) on
                const alwaysGrantedDefaults = {};

                [...companyPermissions, ...departmentPermissions].forEach(group => group.permissions
                  .filter(perm => perm.alwaysGranted)
                  .forEach(perm => { alwaysGrantedDefaults[perm.inputName] = true; }));

                reset(alwaysGrantedDefaults);
                setCreating(true);
              }}
            >
              Create New Role
            </Button>
          </Box>
        </HStack>
      </Box>

      <Box borderBottom="1px solid" borderBottomColor="greyScale.200" p={8}>
        <Text mb={4} textAlign="left" textStyle="bodyMd500">Custom Roles:</Text>

        <SimpleGrid columns={2} spacing={4}>
          {data.map(role => {
            // Don't show default PINS roles
            if (!role.companyId) {
              return null;
            }

            return (
              <Box
                _hover={{ borderColor: colors.primaryScale['400'], cursor: 'pointer' }}
                border="1px solid"
                borderColor="greyScale.200"
                borderRadius="sm"
                boxShadow={boxStyles.boxShadow}
                key={role.id}
                p={8}
                onClick={() => onViewRole(role)}
              >
                <HStack justifyContent="space-between">
                  <Text textStyle="bodyMd500">{role.displayName}</Text>

                  <HStack spacing={2}>
                    <IconButton
                      aria-label="delete-custom-role-button"
                      icon={<Icon as={BsTrash} />}
                      size="sm"
                      variant="textError"
                      onClick={e => {
                        e.stopPropagation();
                        onDelete(role.id);
                      }}
                    />
                  </HStack>
                </HStack>

                {role.description && (
                  <Text color="greyScale.500" mt={2} noOfLines={2} textStyle="bodySm">
                    {role.description}
                  </Text>
                )}

                <Text color="greyScale.400" mt={2} textStyle="bodySm">
                  {(role.permissions || []).length}
                  {' '}
                  permission
                  {(role.permissions || []).length !== 1 ? 's' : ''}
                </Text>
              </Box>
            );
          })}
        </SimpleGrid>
      </Box>

    </Box>
  );
};

CustomRolesPanel.propTypes = {};

export default CustomRolesPanel;
